import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiCreditCard, FiCheck } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

const ClientePagar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const facturaId = searchParams.get('factura');

  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [metodoPago, setMetodoPago] = useState('');
  const [referencia, setReferencia] = useState('');
  const [monto, setMonto] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFacturasPendientes();
  }, [id]);

  useEffect(() => {
    if (facturaId && facturasPendientes.length > 0) {
      const facturaToSelect = facturasPendientes.find(f => f.id === parseInt(facturaId));
      if (facturaToSelect) {
        setSelectedFacturas([facturaToSelect.id]);
      }
    }
  }, [facturaId, facturasPendientes]);

  const loadFacturasPendientes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener facturas pendientes del cliente
      const result = await clientesService.getFacturasCliente(id, {
        payment_status: 'unpaid', // Solo facturas sin pagar
        skip: 0,
        limit: 20
      });

      if (result.success) {
        // Manejar diferentes estructuras de respuesta
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data?.items || result.data?.invoices || []);

        const facturas = dataArray.map(f => ({
          id: f.id,
          numeroFactura: f.invoice_number || `INV-${f.id}`,
          fecha: f.invoice_date || f.created_at,
          fechaVencimiento: f.due_date,
          total: f.total || f.total_amount || 0,
          saldoPendiente: (f.total || f.total_amount || 0) - (f.amount_paid || 0),
          estado: f.status || 'unpaid'
        }));

        setFacturasPendientes(facturas);
      } else {
        setError(result.message || 'Error al cargar las facturas');
      }
    } catch (error) {
      console.error('Error cargando facturas:', error);
      setError('Error de conexión al cargar las facturas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleFactura = (facturaId) => {
    setSelectedFacturas(prev =>
      prev.includes(facturaId)
        ? prev.filter(id => id !== facturaId)
        : [...prev, facturaId]
    );
  };

  const handleProcesarPago = async () => {
    if (selectedFacturas.length === 0 || !metodoPago) {
      alert('Por favor selecciona al menos una factura y un método de pago');
      return;
    }

    setIsSaving(true);
    try {
      // Simular procesamiento de pago
      await new Promise(resolve => setTimeout(resolve, 1000));

      alert('Pago procesado exitosamente');
      navigate(`/system/clientes/${id}/pagos`);
    } catch (error) {
      console.error('Error procesando pago:', error);
      alert('Error al procesar el pago');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando facturas..." />;
  }

  const totalSeleccionado = facturasPendientes
    .filter(f => selectedFacturas.includes(f.id))
    .reduce((sum, f) => sum + (f.saldoPendiente || f.total), 0);

  return (
    <div className="cliente-pagar-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}/estado-cuenta`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">PAGAR FACTURAS</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Facturas pendientes */}
      <div className="facturas-section">
        <h3 className="section-title">Selecciona las facturas a pagar</h3>

        {facturasPendientes.length === 0 ? (
          <div className="empty-state">
            <p>No hay facturas pendientes para este cliente</p>
          </div>
        ) : (
          <div className="facturas-seleccion">
            {facturasPendientes.map((factura) => (
              <div
                key={factura.id}
                className={`factura-seleccion-item ${selectedFacturas.includes(factura.id) ? 'selected' : ''}`}
                onClick={() => handleToggleFactura(factura.id)}
              >
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={selectedFacturas.includes(factura.id)}
                    onChange={() => handleToggleFactura(factura.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div className="factura-info">
                  <div className="factura-numero">{factura.numeroFactura}</div>
                  <div className="factura-fecha">
                    Fecha: {factura.fecha ? new Date(factura.fecha).toLocaleDateString('es-CO') : 'N/A'}
                  </div>
                  {factura.fechaVencimiento && (
                    <div className="factura-vencimiento" style={{ fontSize: '0.85em', color: '#6b7280' }}>
                      Vence: {new Date(factura.fechaVencimiento).toLocaleDateString('es-CO')}
                    </div>
                  )}
                </div>

                <div className="factura-monto">
                  <div style={{ fontWeight: 'bold' }}>
                    ${(factura.saldoPendiente || factura.total).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                  </div>
                  {factura.saldoPendiente !== factura.total && (
                    <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                      Total: ${factura.total.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>

                {selectedFacturas.includes(factura.id) && (
                  <FiCheck className="check-icon" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulario de pago */}
      <div className="pago-form-section">
        <h3 className="section-title">Información del pago</h3>

        <div className="form-group">
          <label className="form-label">Método de Pago</label>
          <select
            className="form-input"
            value={metodoPago}
            onChange={(e) => setMetodoPago(e.target.value)}
          >
            <option value="">Selecciona un método</option>
            <option value="efectivo">Efectivo</option>
            <option value="cheque">Cheque</option>
            <option value="transferencia">Transferencia Bancaria</option>
            <option value="tarjeta">Tarjeta de Crédito/Débito</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Referencia / Número de Transacción</label>
          <input
            type="text"
            className="form-input"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            placeholder="Ej: CHK-1234, TRF-5678"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Monto (opcional para pago parcial)</label>
          <input
            type="number"
            className="form-input"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            placeholder={`Total: $${totalSeleccionado.toLocaleString()}`}
            step="0.01"
          />
        </div>
      </div>

      {/* Resumen */}
      <div className="pago-resumen">
        <div className="resumen-item">
          <span>Facturas seleccionadas:</span>
          <strong>{selectedFacturas.length}</strong>
        </div>
        <div className="resumen-item total">
          <span>Total a pagar:</span>
          <strong className="total-monto">
            ${(monto || totalSeleccionado).toLocaleString()}
          </strong>
        </div>
      </div>

      {/* Botones */}
      <div className="form-actions">
        <button
          onClick={() => navigate(`/system/clientes/${id}/estado-cuenta`)}
          className="btn btn--outline"
          disabled={isSaving}
        >
          Cancelar
        </button>
        <button
          onClick={handleProcesarPago}
          className="btn btn--primary"
          disabled={isSaving || selectedFacturas.length === 0}
        >
          {isSaving ? (
            <>
              <div className="loading-spinner-single" style={{ width: '16px', height: '16px' }}></div>
              Procesando...
            </>
          ) : (
            <>
              <FiDollarSign /> Procesar Pago
            </>
          )}
        </button>
      </div>

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/clientes/${id}`} currentTab="pagar" />
    </div>
  );
};

export default ClientePagar;
